import { Button, Form, message, Modal, Radio, Space } from 'antd';
import i18n from 'i18n';
import { OmsOrderDetail } from 'types/oms';
import printOrderReceipt from 'utils/print-order-receipt';
import usePosPrinter from 'commons/hooks/use-pos-printer';
import useResource from 'commons/hooks/useResource';
import { useToggle } from 'react-use';
import { useState } from 'react';
import {
  CURRENCY_MAP,
  CurrencyType,
  isCurrencySupported,
} from 'constant/currency';

// 当前支持打印小票：日本、香港、新加坡、迪拜、法国、上海、纽约
const RECEIPT_PRINT_SUPPORTED_CURRENCIES: CurrencyType[] = [
  CURRENCY_MAP.JPY,
  CURRENCY_MAP.HKD,
  CURRENCY_MAP.SGD,
  CURRENCY_MAP.AED,
  CURRENCY_MAP.EUR,
  CURRENCY_MAP.CNY,
  CURRENCY_MAP.USD,
];

const PrintReceiptButton = ({
  omsOrderDetail,
  onPrintReceiptByBrowser,
  isB2B = false,
}: {
  omsOrderDetail: OmsOrderDetail;
  onPrintReceiptByBrowser: (options?: { addSign?: boolean }) => void;
  /** B2B 订单需具备 order-detail-print-b2b-receipt 权限才能打印小票 */
  isB2B?: boolean;
}) => {
  const { orderItemList } = omsOrderDetail;
  const actualCurrency = orderItemList[0]?.actualCurrency;
  const { posPrinterInfo } = usePosPrinter();
  const canPrintB2BReceipt = useResource('order-detail-print-b2b-receipt');
  const [open, toggleOpen] = useToggle(false);
  const [addSign, toggleAddSign] = useToggle(true);
  const [loading, setLoading] = useState(false);
  const [printTarget, setPrintTarget] = useState<'printer' | 'browser'>(
    'printer',
  );

  if (isB2B && !canPrintB2BReceipt) return null;
  if (!actualCurrency) return null;
  if (!isCurrencySupported(actualCurrency)) return null;
  if (!RECEIPT_PRINT_SUPPORTED_CURRENCIES.includes(actualCurrency)) return null;

  return (
    <>
      <Space>
        <Button
          loading={loading}
          type="primary"
          onClick={async () => {
            setPrintTarget('printer');
            toggleOpen();
          }}
        >
          {i18n.t('print_receipt')}
        </Button>

        <Button
          type="primary"
          onClick={async () => {
            setPrintTarget('browser');
            toggleOpen();
          }}
        >
          {i18n.t('NlBduEDffi')}
        </Button>
      </Space>

      <Modal
        okButtonProps={{
          loading:
            printTarget === 'printer' && posPrinterInfo.status === 'connecting',
        }}
        title={i18n.t('receipt_print_dialog_title')}
        open={open}
        onCancel={toggleOpen}
        onOk={() => {
          if (printTarget === 'browser') {
            onPrintReceiptByBrowser({ addSign });
          } else {
            setLoading(true);
            printOrderReceipt({ orderId: omsOrderDetail.id, addSign })
              .catch(() => {
                message.error(i18n.t('receipt_print_failed'));
              })
              .finally(() => {
                setLoading(false);
              });
          }

          toggleOpen();
        }}
      >
        <Form.Item label={i18n.t('receipt_print_add_signature_field')}>
          <Radio.Group
            value={addSign}
            onChange={(v) => {
              toggleAddSign(v.target.value);
            }}
          >
            <Radio value={true}>{i18n.t('yes')}</Radio>
            <Radio value={false}>{i18n.t('no')}</Radio>
          </Radio.Group>
        </Form.Item>
      </Modal>
    </>
  );
};

export default PrintReceiptButton;
